import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { helpDocumentation } from "@/content/helpDocumentation";

type HelpDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const HelpDialog = ({ open, onOpenChange }: HelpDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{helpDocumentation.title}</DialogTitle>
          <DialogDescription>{helpDocumentation.subtitle}</DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-2 max-h-[70vh] space-y-6">
          <section className="space-y-3">
            {helpDocumentation.intro.map((line) => (
              <p key={line} className="text-sm text-muted-foreground">
                {line}
              </p>
            ))}
          </section>

          <Separator />

          <section className="space-y-3">
            <h3 className="text-base font-semibold">{helpDocumentation.quickStart.title}</h3>
            <ol className="list-decimal space-y-2 pl-5 text-sm">
              {helpDocumentation.quickStart.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>

          <Separator />

          <section className="space-y-4">
            <h3 className="text-base font-semibold">Detailed guide</h3>
            <Accordion type="multiple" className="w-full">
              {helpDocumentation.sections.map((section) => (
                <AccordionItem key={section.id} value={section.id}>
                  <AccordionTrigger>{section.title}</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 text-sm">
                      <p className="text-muted-foreground">{section.summary}</p>
                      {section.bullets && section.bullets.length > 0 && (
                        <ul className="list-disc space-y-2 pl-5">
                          {section.bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      )}
                      {section.steps && section.steps.length > 0 && (
                        <ol className="list-decimal space-y-2 pl-5">
                          {section.steps.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                      )}
                      {section.tips && section.tips.length > 0 && (
                        <div className="rounded-md border bg-muted/40 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tips</p>
                          <ul className="mt-2 list-disc space-y-1 pl-4">
                            {section.tips.map((tip) => (
                              <li key={tip}>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <Separator />

          <section className="space-y-3">
            <h3 className="text-base font-semibold">FAQ</h3>
            <div className="space-y-3">
              {helpDocumentation.faq.map((item) => (
                <div key={item.question} className="rounded-md border p-3">
                  <p className="text-sm font-semibold">{item.question}</p>
                  <p className="text-sm text-muted-foreground mt-1">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <Separator />

          <section className="space-y-3">
            <h3 className="text-base font-semibold">{helpDocumentation.support.title}</h3>
            <ul className="list-disc space-y-2 pl-5 text-sm">
              {helpDocumentation.support.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog;
